import useChartPayload, { TooltipPayloads } from '../../hooks/useChartPayload'
import {
    NyuDataPoint,
    NyuDataProperties,
    NyuLineProperties,
    NyuSeriesProperties,
} from '../../../../shared/types'
import { cn } from '../../utils/cn'
import {
    formatDataPointValue,
    isForecastDataKey,
    toBaseDataKey,
} from '../../utils/helpers'
import DataKeyLabel from './DataKeyLabel'
import { useHighContrastDisplay } from '../../hooks/useHighContrastMode'
import { SHAPES } from './HighContrastMode/constants'
import { getLetterIdentifier } from './HighContrastMode/BarLabel'

function Row({
    label,
    asterisk = false,
    bold = false,
    children,
}: {
    label: string | React.ReactNode
    asterisk?: boolean
    bold?: boolean
    children: React.ReactNode
}) {
    return (
        <>
            <div className={cn(bold ? 'font-medium' : '', 'mr-3')}>{label}</div>
            <div className={cn(bold ? 'font-medium' : '', 'text-right')}>
                {children}{' '}
                <span className="inline-block w-2 text-gray-400">
                    {asterisk ? '*' : ' '}
                </span>
            </div>
        </>
    )
}

function DataRow({
    dataKey,
    lineData,
    children,
    highContrastMode,
    shapeIndex,
    seriesProperties,
    isBarChart,
    usedDataKeys,
}: {
    dataKey: string
    lineData?: NyuLineProperties
    children: React.ReactNode
    highContrastMode?: boolean
    shapeIndex?: number
    seriesProperties: NyuSeriesProperties
    isBarChart?: boolean
    usedDataKeys?: string[]
}) {
    const shapeType =
        highContrastMode && shapeIndex !== undefined && !isBarChart
            ? SHAPES[shapeIndex % SHAPES.length]
            : undefined

    const letterIdentifier =
        highContrastMode && isBarChart
            ? getLetterIdentifier(dataKey, seriesProperties, usedDataKeys)
            : undefined

    if (!lineData) {
        return null
    }

    return (
        <Row
            key={dataKey}
            label={
                <DataKeyLabel
                    lineData={lineData!}
                    highContrastMode={highContrastMode}
                    shapeType={shapeType}
                    letterIdentifier={letterIdentifier}
                    isBarChart={isBarChart}
                />
            }
            // bar forecasts are explained by their legend item instead
            asterisk={!isBarChart && isForecastDataKey(dataKey)}
        >
            {children}
        </Row>
    )
}

function TooltipEntry({
    dataKeys,
    payload,
    dataProperties,
    getLineData,
    seriesProperties,
}: {
    dataKeys: string[]
    payload?: NyuDataPoint
    dataProperties: NyuDataProperties
    getLineData: (dataKey: string) => NyuLineProperties
    seriesProperties: NyuSeriesProperties
}) {
    const highContrastDisplay = useHighContrastDisplay(
        dataProperties,
        seriesProperties
    )

    if (!payload) {
        return null
    }

    const sortedDataKeys = dataKeys.toSorted(
        (a, b) => Number(payload[b]) - Number(payload[a])
    )

    const usedDataKeys = highContrastDisplay.isBarChart
        ? dataKeys
        : sortedDataKeys

    const hasForecastKeys =
        !highContrastDisplay.isBarChart && dataKeys.every(isForecastDataKey)

    const hasMultipleAxes = !!dataProperties.secondaryAxis
    const getDataFormat = (
        hasMultipleAxes: boolean,
        lineData: NyuLineProperties
    ) => {
        if (!lineData) {
            console.log(
                'getDataFormat.lineData not found, returning dataProperties.format',
                dataProperties.format
            )
            return dataProperties.format
        }
        if (hasMultipleAxes && lineData.secondaryAxis) {
            return dataProperties.secondaryAxis!.format
        }

        return dataProperties.format
    }

    // Create a mapping function to get the correct shape index for each dataKey
    const getShapeIndex = (dataKey: string): number => {
        // Find the index in seriesProperties for the current dataKey
        const seriesIndex = seriesProperties.findIndex(
            (series) => series.key === toBaseDataKey(dataKey)
        )
        return seriesIndex >= 0 ? seriesIndex : 0
    }

    return (
        <div className="bg-white p-5 leading-relaxed shadow-lg">
            <div
                className="grid grid-cols-2"
                style={{ gridTemplateColumns: 'auto auto' }}
            >
                {payload.tooltipTimeValue && (
                    <Row label={payload.tooltipTimeLabel} bold>
                        {payload.tooltipTimeValue}
                    </Row>
                )}
                {usedDataKeys.map((dataKey) => (
                    <DataRow
                        key={dataKey}
                        dataKey={dataKey}
                        lineData={getLineData(dataKey)!}
                        highContrastMode={highContrastDisplay.highContrastMode}
                        shapeIndex={
                            highContrastDisplay.showShapes
                                ? getShapeIndex(dataKey)
                                : undefined
                        }
                        seriesProperties={seriesProperties}
                        isBarChart={highContrastDisplay.isBarChart}
                        usedDataKeys={usedDataKeys}
                    >
                        {formatDataPointValue(
                            Number(payload[dataKey]),
                            getDataFormat(hasMultipleAxes, getLineData(dataKey))
                        )}
                    </DataRow>
                ))}
            </div>
            {hasForecastKeys && (
                <div className="text-right text-gray-400">
                    * = forecasted values
                </div>
            )}
        </div>
    )
}

type CustomTooltipProps = {
    dataProperties: NyuDataProperties
    seriesProperties: NyuSeriesProperties
    payload?: TooltipPayloads
    active?: boolean
}

export default function CustomTooltip({
    dataProperties,
    seriesProperties,
    payload = [],
    active = false,
}: CustomTooltipProps) {
    const { dataKeys, getLineData } = useChartPayload({
        payload,
        seriesProperties,
    })

    if (!active || payload.length === 0) {
        return null
    }

    const [firstPayload] = payload

    const allStacked = seriesProperties.every(
        (series) => series.stack === 'all'
    )
    const nextDataKeys = allStacked ? dataKeys.toReversed() : dataKeys

    return (
        <TooltipEntry
            {...firstPayload}
            dataKeys={nextDataKeys}
            dataProperties={dataProperties}
            getLineData={getLineData}
            seriesProperties={seriesProperties}
        />
    )
}
